import "./App.css";
import { Routes, Route } from "react-router-dom";
import { useRef, useEffect, useContext } from "react";
import LoginRedirect from "./components/LoginRedirect";
import LoginHybrid from "./components/LoginHybrid";
import LoginWrapper from "./components/LoginWrapper";
import { ConfigContext } from "./util/ConfigContext";
import { ThemeContext } from "./util/ThemeContext";
import { loadTenantBranding } from "./util/tenantBranding";
import Dashboard from "./components/Dashboard";
import Downloads from "./components/Downloads";
import ContactUs from "./components/ContactUs";
import RedirectHandler from "./components/RedirectHandler";
import Schedule from "./components/subscriber/Schedule";
import Sidebar from "./components/Sidebar";
import PrivacyPolicy from "./components/PrivacyPolicy";
import Zoom from "./components/subscriber/Zoom";
import NotFoundPage from "./components/NotFoundPage";
import { tailChase } from "ldrs";
import Header from "./components/Header";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import detectedPlatform from "./util/PlatformUtils";
import appUpdatesConfig from "../src/config/appUpdatesConfig.json";

tailChase.register();

function AppRoutes({ dashboardRef }) {
  return (
    <>
      <Routes>
        <Route path="/" element={<LoginWrapper />} />
        <Route path="/universal/login" element={<LoginHybrid />} />
        <Route path="/dashboard" element={<Dashboard ref={dashboardRef} />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/downloads" element={<Downloads />} />
        <Route path="/redirect" element={<RedirectHandler />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/schedule" element={<Schedule />} />
        <Route path="/zoom" element={<Zoom />} />
        <Route path="/auth/redirect" element={<LoginRedirect />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}

function App() {
  const dashboardRef = useRef({});
  const { setVersionInfo } = useContext(ConfigContext);
  const themeData = useContext(ThemeContext);

  useEffect(() => {
    // 🔹 Load tenant-specific favicon / manifest / theme
    loadTenantBranding(themeData);
    const detectPlatform = detectedPlatform();

    if (detectPlatform === "windows") {
      const messageHandler = (event) => {
        const rawData = event.data;
        switch (rawData["command"]) {
          case "getDeviceInfo":
            const packageName = rawData["appId"];
            const versionInfo = rawData["appVersion"];
            setVersionInfo(versionInfo);

            const appPackageUpdatesConfig =
              appUpdatesConfig[packageName] || appUpdatesConfig["default"];

            const windowsConfig = appPackageUpdatesConfig?.windows;
            if (windowsConfig && windowsConfig.url && windowsConfig.version) {
              window.nativeBridge.checkForUpdates(
                windowsConfig.version,
                "https://multiapp.ac" + windowsConfig.url,
                false
              );
            }
            break;

          default:
            break;
        }
      };

      // Register listener
      if (window.chrome?.webview) {
        window.chrome.webview.addEventListener("message", messageHandler);
      }

      // Cleanup on unmount
      return () => {
        if (window.chrome?.webview) {
          window.chrome.webview.removeEventListener("message", messageHandler);
        }
      };
    }

    // ✅ iOS version callback handler
    if (detectPlatform === "ios") {
      window.onReceiveAppVersion = function (appInfo) {
        console.log("✅ App Version from Swift:", appInfo);
        setVersionInfo(appInfo.version);
      };
    }
  }, []);

  useEffect(() => {
    const platform = detectedPlatform();
    if (platform === "android") {
      const deviceInfoStr = window.ZoomHelper.getDeviceInfo();
      const deviceInfo = JSON.parse(deviceInfoStr);
      const packageName = deviceInfo?.app?.package_name;
      if (packageName) {
        const appPackageUpdatesConfig =
          appUpdatesConfig[packageName] || appUpdatesConfig["default"];
        if (appPackageUpdatesConfig) {
          const android = appPackageUpdatesConfig?.android;
          if (android && android?.url && android?.version) {
            window.ZoomHelper.checkForAppUpdate(
              android.version,
              window.location.origin + android.url,
              false
            );
            return;
          }
        }
      }
      window.ZoomHelper.showToast("ERROR: Cannot check for App Update");
    } else if (platform === "windows") {
      window.nativeBridge.getDeviceInfo();
    } else if (platform === "ios") {
      if (window.webkit?.messageHandlers?.getVersion) {
        window.webkit.messageHandlers.getVersion.postMessage(null);
      }
    }
  }, []);

  return (
    <div className="App flex flex-col min-h-screen ">
      <ToastContainer />
      <Header dashboardRef={dashboardRef} />

      <div className="flex flex-1 ">
        <Sidebar ref={dashboardRef} />

        <div className="flex-1 overflow-auto">
          <AppRoutes dashboardRef={dashboardRef} />
        </div>
      </div>
    </div>
  );
}

export default App;
